import React, { useState } from 'react';
import { Card } from '../../../design-system/Card';
import { Button } from '../../../design-system/Button';
import { Badge } from '../../../design-system/Badge';
import { Tabs } from '../../../design-system/Tabs';
import { Modal } from '../../../design-system/Modal';
import { Input } from '../../../design-system/Input';
import {
  Lightbulb,
  Target,
  Sparkles,
  Layers,
  Plus
} from 'lucide-react';

export const ProductView = () => {
  const [activeTab, setActiveTab] = useState('rice');
  const [isNewFeatureModalOpen, setIsNewFeatureModalOpen] = useState(false);

  const [featureName, setFeatureName] = useState('');
  const [reach, setReach] = useState('1200');
  const [impact, setImpact] = useState('3'); // 3 = High
  const [confidence, setConfidence] = useState('80');
  const [effort, setEffort] = useState('2');

  const tabs = [
    { id: 'rice', label: 'RICE Prioritization Matrix', icon: <Target size={15} /> },
    { id: 'vision', label: 'Strategic Pillars & North Star', icon: <Lightbulb size={15} /> },
    { id: 'discovery', label: 'Opportunity Solution Tree', icon: <Layers size={15} /> }
  ];

  const [features, setFeatures] = useState([
    {
      id: 'FEAT-101',
      name: 'Realtime Bi-directional Google Calendar Sync',
      reach: 2400,
      impact: 3, // High (3x)
      confidence: 0.9,
      effort: 2,
      score: Math.round((2400 * 3 * 0.9) / 2), // 3240
      status: 'In Sprints'
    },
    {
      id: 'FEAT-102',
      name: 'Interactive Hardware BOM Excel & CSV Exporter',
      reach: 850,
      impact: 2, // Medium (2x)
      confidence: 0.85,
      effort: 1,
      score: Math.round((850 * 2 * 0.85) / 1), // 1445
      status: 'Approved'
    },
    {
      id: 'FEAT-103',
      name: 'Dark Mode AMOLED Pitch-Black Option',
      reach: 3200,
      impact: 1, // Low (1x)
      confidence: 0.95,
      effort: 1,
      score: Math.round((3200 * 1 * 0.95) / 1), // 3040
      status: 'Candidate'
    }
  ]);

  const handleAddFeature = (e) => {
    e?.preventDefault();
    if (!featureName.trim()) return;

    const r = parseFloat(reach) || 500;
    const imp = parseFloat(impact) || 2;
    const conf = (parseFloat(confidence) || 80) / 100;
    const eff = parseFloat(effort) || 2;
    const sc = Math.round((r * imp * conf) / eff);

    setFeatures([
      ...features,
      {
        id: `FEAT-${Math.floor(104 + Math.random() * 800)}`,
        name: featureName.trim(),
        reach: r,
        impact: imp,
        confidence: conf,
        effort: eff,
        score: sc,
        status: 'Candidate'
      }
    ]);
    setFeatureName('');
    setIsNewFeatureModalOpen(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }} className="animate-fade-in">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <Badge variant="teal" size="sm">Product Methodology</Badge>
            <h2 style={{ fontSize: '20px', fontWeight: '800' }}>Product Strategy & Discovery Suite</h2>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
            RICE quantitative scoring, strategic opportunity solution trees, and customer discovery
          </p>
        </div>

        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
      </div>

      {/* TAB 1: RICE MATRIX */}
      {activeTab === 'rice' && (
        <Card padding="20px">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '800' }}>RICE Feature Prioritization Matrix</h3>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                Formula: <code>(Reach × Impact × Confidence) / Effort = RICE Score</code>
              </p>
            </div>

            <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => setIsNewFeatureModalOpen(true)}>
              Add Candidate Feature
            </Button>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-divider)', color: 'var(--text-muted)', fontSize: '11px', textTransform: 'uppercase' }}>
                  <th style={{ padding: '10px 14px' }}>Feature Candidate</th>
                  <th style={{ padding: '10px 14px' }}>Reach</th>
                  <th style={{ padding: '10px 14px' }}>Impact</th>
                  <th style={{ padding: '10px 14px' }}>Confidence</th>
                  <th style={{ padding: '10px 14px' }}>Effort</th>
                  <th style={{ padding: '10px 14px' }}>RICE Score</th>
                  <th style={{ padding: '10px 14px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {features
                  .sort((a, b) => b.score - a.score)
                  .map((f) => (
                    <tr key={f.id} style={{ borderBottom: '1px solid var(--border-divider)' }}>
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ fontWeight: '700', color: 'var(--text-primary)' }}>{f.name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{f.id}</div>
                      </td>
                      <td style={{ padding: '12px 14px' }}>{f.reach} users</td>
                      <td style={{ padding: '12px 14px' }}>{f.impact}x</td>
                      <td style={{ padding: '12px 14px' }}>{Math.round(f.confidence * 100)}%</td>
                      <td style={{ padding: '12px 14px' }}>{f.effort} wks</td>
                      <td style={{ padding: '12px 14px', fontSize: '15px', fontWeight: '900', color: 'var(--accent-teal)' }}>
                        {f.score}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <Badge variant={f.status === 'In Sprints' ? 'success' : f.status === 'Approved' ? 'teal' : 'neutral'} size="xs">
                          {f.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* TAB 2: VISION */}
      {activeTab === 'vision' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
          <Card padding="22px">
            <h4 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '8px' }}>North Star Metric</h4>
            <div style={{ fontSize: '24px', fontWeight: '900', color: 'var(--accent-teal)', marginBottom: '8px' }}>
              Weekly Active Projects
            </div>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
              Percentage of teams executing at least 1 sprint, milestone, or task state transition per week.
            </p>
          </Card>

          <Card padding="22px">
            <h4 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '8px' }}>Zero Vendor Lock-in (ADR-029)</h4>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
              Users maintain 100% data ownership with 1-click JSON, CSV, and markdown export anytime.
            </p>
          </Card>
        </div>
      )}

      {/* TAB 3: DISCOVERY */}
      {activeTab === 'discovery' && (
        <Card padding="24px">
          <h3 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '14px' }}>Opportunity Solution Tree (OST)</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ padding: '14px 16px', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
              <div style={{ fontSize: '11px', fontWeight: '800', color: 'var(--accent-teal)', textTransform: 'uppercase' }}>CUSTOMER PAIN POINT</div>
              <div style={{ fontSize: '14px', fontWeight: '700', marginTop: '2px' }}>Teams get overwhelmed when forced into fixed Jira-style rigid workflows.</div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                <strong>Forma Solution:</strong> Zero lock-in modular toggle engine + 10 discipline methodology packs.
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* ADD CANDIDATE MODAL */}
      <Modal
        isOpen={isNewFeatureModalOpen}
        onClose={() => setIsNewFeatureModalOpen(false)}
        title="Add Feature Candidate"
        subtitle="Evaluate RICE score for product backlog prioritization"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsNewFeatureModalOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleAddFeature} icon={<Sparkles size={14} />}>Add Candidate</Button>
          </>
        }
      >
        <form onSubmit={handleAddFeature} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <Input
            label="Feature Name *"
            placeholder="e.g., Live Bi-Directional Figma Component Sync"
            value={featureName}
            onChange={(e) => setFeatureName(e.target.value)}
            required
            autoFocus
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <Input label="Reach (Users/mo)" value={reach} onChange={(e) => setReach(e.target.value)} />
            <Input label="Impact (1-3x)" value={impact} onChange={(e) => setImpact(e.target.value)} />
            <Input label="Confidence (%)" value={confidence} onChange={(e) => setConfidence(e.target.value)} />
            <Input label="Effort (Weeks)" value={effort} onChange={(e) => setEffort(e.target.value)} />
          </div>
        </form>
      </Modal>
    </div>
  );
};
